<template>
  <article
    class="about-self-biography"
    :class="{ 'about-self-biography--dark': $vuetify.theme.dark }"
    lang="en"
    aria-labelledby="about-self-biography-title"
  >
    <header class="about-hero">
      <p class="about-eyebrow">
        {{ $t("\n        What is Self Biography?\n      ") }}
      </p>
      <h1 id="about-self-biography-title">
        {{ $t("\n        Your life is a story") }}<br class="about-desktop-break" /> <span>{{ $t("worth preserving.") }}</span>
      </h1>
      <div class="about-intro">
        <p>{{ $t("The first of its kind, Self Biography is an archival, privacy-centric social platform where you can document your life and create an official biographical record to preserve for yourself, the people you love, and future generations.") }}</p>
        <p>{{ $t("Record your memories, experiences, accomplishments and life's work using words, photographs, video, voice, documents and other media. Self Biography brings everything together chronologically, helping you gradually assemble the story of your life—one memory at a time.") }}</p>
      </div>
      <p class="about-hero-note">
        {{ $t("\n        Every memory is a piece. ") }}<strong>{{ $t("Your life is the puzzle.") }}</strong>
      </p>
    </header>

    <section
      class="about-section about-reading"
      aria-labelledby="more-than-memories"
    >
      <p class="about-eyebrow">
        {{ $t("\n        More than memories\n      ") }}
      </p>
      <h2 id="more-than-memories">
        {{ $t("\n        A life is more than a collection of photographs.\n      ") }}
      </h2>
      <p>{{ $t("It is the people you knew, the places you went, the things you created, the work you accomplished, the mistakes you made, the lessons you learned, and the moments—large and small—that made you who you are.") }}</p>
      <p>{{ $t("Self Biography gives all those pieces a place to belong.") }}</p>
      <p>{{ $t("Some may eventually become part of the story you share with the world. Others may remain private forever. ") }}<strong>{{ $t("Preserving a memory does not mean you have to publish it.") }}</strong></p>
    </section>

    <section
      class="about-section about-roles"
      aria-labelledby="your-story"
    >
      <div class="about-section-heading">
        <h2 id="your-story">
          {{ $t("\n          You are the author, curator, and editor of your life\n        ") }}
        </h2>
        <p>{{ $t("Self Biography provides the tools, but you remain in control of the story.") }}</p>
      </div>
      <div class="about-role-grid">
        <div
          v-for="role in roles"
          :key="role.title"
          class="about-role"
        >
          <v-icon
            class="about-role-icon"
            aria-hidden="true"
          >
            {{ role.icon }}
          </v-icon>
          <h3>{{ role.title }}</h3>
          <p>{{ role.description }}</p>
        </div>
      </div>
      <p class="about-ai-note">
        {{ $t("\n        Self Biography and its AI tools can help you research, organize, restore, translate, write and edit, but you make the final decisions.\n      ") }}
      </p>
      <div class="about-principles">
        <p><strong>{{ $t("Preserve") }}</strong>{{ $t(" what you want to remember.") }}</p>
        <p><strong>{{ $t("Curate") }}</strong>{{ $t(" what you want others to see.") }}</p>
        <p><strong>{{ $t("Edit") }}</strong>{{ $t(" the story you want to tell.") }}</p>
        <p><strong>{{ $t("Publish") }}</strong>{{ $t(" only what you choose.") }}</p>
      </div>
    </section>

    <section
      class="about-section"
      aria-labelledby="personal-and-professional"
    >
      <div class="about-section-heading">
        <h2 id="personal-and-professional">
          {{ $t("\n          Your personal life and your life's work\n        ") }}
        </h2>
        <p class="about-lead">
          {{ $t("\n          Who you are and what you have done are different parts of the same story.\n        ") }}
        </p>
        <p>{{ $t("Self Biography allows you to build both a Personal Biography and a Professional Biography, connected through the same chronological life.") }}</p>
      </div>
      <div class="about-biographies">
        <div class="about-biography">
          <v-icon
            class="about-role-icon"
            aria-hidden="true"
          >
            {{ icons.mdiAccountOutline }}
          </v-icon>
          <h3>{{ $t("Personal Biography") }}</h3>
          <p>{{ $t("Your Personal Biography can preserve family, childhood, friendships, relationships, travels, places, experiences, thoughts and everyday memories.") }}</p>
        </div>
        <div class="about-biography">
          <v-icon
            class="about-role-icon"
            aria-hidden="true"
          >
            {{ icons.mdiBriefcaseOutline }}
          </v-icon>
          <h3>{{ $t("Professional Biography") }}</h3>
          <p>{{ $t("Your Professional Biography can preserve your career, artwork, businesses, projects, inventions, publications, exhibitions, awards, accomplishments and complete body of work.") }}</p>
        </div>
      </div>
      <p class="about-section-ending">
        {{ $t("\n        Together they create something more complete than either a traditional biography or a professional portfolio: ") }}<strong>{{ $t("the person, the work, and the life behind both.") }}</strong>
      </p>
    </section>

    <section
      class="about-section about-reading"
      aria-labelledby="where-to-begin"
    >
      <h2 id="where-to-begin">
        {{ $t("\n        Your life doesn't have to begin at the beginning\n      ") }}
      </h2>
      <p>{{ $t("Start today. Start with childhood. Start with your career. Or start with something that happened fifty years ago.") }}</p>
      <p class="about-lead">
        {{ $t("\n        There is no correct place to begin.\n      ") }}
      </p>
      <p>{{ $t("Add the pieces as you remember them. Connect them to people, places, dates and events. Ask others who shared those moments what they remember.") }}</p>
      <p>{{ $t("Over time, individual Memory Bits begin connecting into something much larger:") }}</p>
      <p class="about-emphasis">
        {{ $t("\n        Your Life Puzzle. Your Biography. Your History.\n      ") }}
      </p>
    </section>

    <section
      class="about-section about-privacy"
      aria-labelledby="different-platform"
    >
      <div class="about-reading">
        <p class="about-eyebrow">
          {{ $t("\n          A different kind of social platform\n        ") }}
        </p>
        <h2 id="different-platform">
          {{ $t("\n          You are always the gatekeeper.\n        ") }}
        </h2>
        <p>{{ $t("Self Biography is not another public social-media feed.") }}</p>
        <p>{{ $t("It is a network of private networks built around people, families, friends and the memories they share.") }}</p>
        <p>{{ $t("There is no requirement to broadcast your life to everyone.") }}</p>
        <p>{{ $t("A photograph can be private while another is public. A personal Memory Bit can remain only for you while another is shared with family. Parts of your professional biography may be public while personal experiences remain completely private.") }}</p>
        <p>{{ $t("Every Memory Bit, photograph, story, video or document can be individually kept private or made public at the click of a button.") }}</p>
        <p class="about-privacy-statement">
          {{ $t("\n          Your private archive and your public biography are not the same thing.\n        ") }}
        </p>
      </div>
    </section>

    <section
      class="about-section about-reading"
      aria-labelledby="why-self-biography"
    >
      <p class="about-eyebrow">
        {{ $t("\n        Why Self Biography?\n      ") }}
      </p>
      <h2 id="why-self-biography">
        {{ $t("\n        Every generation loses an extraordinary amount of human history.\n      ") }}
      </h2>
      <p>{{ $t("Not only the lives of famous people, but the experiences of parents and grandparents, artists and inventors, immigrants and travelers, workers and entrepreneurs, thinkers and dreamers—ordinary people who lived extraordinary moments and left something behind.") }}</p>
      <p class="about-reflection">
        {{ $t("\n        What did they know? What did they create? What did they believe? What made them laugh? What mistakes did they make? What did they learn that might still be useful to us?\n      ") }}
      </p>
      <p>{{ $t("Most of it disappears.") }}</p>
      <p>{{ $t("Self Biography was created to help change that.") }}</p>
    </section>

    <footer class="about-closing">
      <h2>{{ $t("You don't have to be famous to have a life worth remembering.") }}</h2>
      <p>{{ $t("Technology can do more than help us broadcast what we think today. It can help us remember, rediscover and preserve who we are—and what we learned while we were here.") }}</p>
      <p class="about-closing-message">
        {{ $t("\n        Bring people together through their shared memories rather than keep them apart by their opinions.\n      ") }}
      </p>
      <v-btn
        color="primary"
        large
        :to="$store.state.logged ? '/my-pictures' : '/auth'"
      >
        {{ $t("\n        Start your story\n      ") }}
      </v-btn>
    </footer>
  </article>
</template>

<script>
import {
  mdiPencilOutline, mdiArchiveOutline, mdiBookOpenPageVariantOutline, mdiAccountOutline, mdiBriefcaseOutline,
} from '@mdi/js'
import { t } from '@/i18n'

export default {
  name: 'SelfBiographyAbout',
  data: () => ({
    previousTitle: '',
    icons: { mdiAccountOutline, mdiBriefcaseOutline },
    roles: [
      {
        get title() { return t('Author') },
        icon: mdiPencilOutline,
        get description() { return t("As the Author, you record your memories, experiences, thoughts, accomplishments and life's work—in your own words and in your own way.") },
      },
      {
        get title() { return t('Curator') },
        icon: mdiArchiveOutline,
        get description() { return t('As the Curator, you build and organize your personal archive. You decide what belongs, connect memories with people, places and events, restore and improve photographs and other material, and decide what remains entirely private and what you are comfortable sharing.') },
      },
      {
        get title() { return t('Editor') },
        icon: mdiBookOpenPageVariantOutline,
        get description() { return t('As the Editor, you can eventually step back and look at the life you have assembled. Select, arrange, polish and refine your Memory Bits into the story you want to present—whether it becomes a public biography, professional portfolio, family history, documentary, digital edition or printed book.') },
      },
    ],
  }),
  mounted() {
    this.previousTitle = document.title
    document.title = t('What is Self Biography? | Self Biography')
    window.scrollTo(0, 0)
  },
  beforeDestroy() {
    document.title = this.previousTitle
  },
}
</script>

<style lang="scss" scoped>
.about-self-biography {
  --about-text: #5e5669;
  --about-heading: #393345;
  --about-accent: #713ac7;
  --about-surface: #fff;
  --about-tint: #f0e9fc;
  --about-divider: rgba(94, 86, 105, 0.16);
  max-width: 1120px;
  margin: 24px auto 56px;
  color: var(--about-text);
  font-size: 16px;
  line-height: 1.8;

  &--dark {
    --about-text: #d0cadf;
    --about-heading: #eee9fa;
    --about-accent: #c6a5ff;
    --about-surface: #312d4b;
    --about-tint: #39304f;
    --about-divider: rgba(231, 227, 252, 0.16);
  }

  h1, h2, h3 { color: var(--about-heading); font-weight: 600; line-height: 1.25; }
  h1 { font-size: clamp(2.15rem, 4.5vw, 3.75rem); letter-spacing: -0.04em; margin-bottom: 28px; }
  h1 span { color: var(--about-accent); }
  h2 { font-size: clamp(1.55rem, 2.7vw, 2.15rem); letter-spacing: -0.025em; margin-bottom: 24px; }
  h3 { font-size: 1.25rem; margin: 16px 0; }
  p { margin-bottom: 20px; }
  strong { font-weight: 600; }
  .about-eyebrow { color: var(--about-accent); font-size: 0.75rem; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 20px; }
}
.about-hero {
  text-align: center;
  padding: 64px 48px 36px;
  background: linear-gradient(150deg, var(--about-surface), var(--about-tint));
  border: 1px solid var(--about-divider);
  border-radius: 12px;
}
.about-intro { max-width: 780px; margin: 0 auto; }
.about-hero-note { border-top: 1px solid var(--about-divider); padding-top: 24px; margin-top: 32px; }
.about-hero-note strong { color: var(--about-accent); }
.about-section { margin: 72px auto; }
.about-reading { max-width: 760px; }
.about-section-heading { max-width: 780px; margin: 0 auto 32px; text-align: center; }
.about-role-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); border-top: 1px solid var(--about-divider); border-bottom: 1px solid var(--about-divider); padding: 32px 0; }
.about-role { padding: 0 28px; }
.about-role + .about-role { border-left: 1px solid var(--about-divider); }
.about-role p { margin-bottom: 0; }
.about-role-icon { color: var(--about-accent) !important; }
.about-ai-note { max-width: 780px; margin: 28px auto 32px; text-align: center; }
.about-principles { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 32px; padding: 32px 40px; background: var(--about-tint); border-radius: 8px; }
.about-principles p { margin: 0; }
.about-principles strong { color: var(--about-accent); }
.about-biographies { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
.about-biography { padding: 32px; background: var(--about-surface); border: 1px solid var(--about-divider); border-radius: 8px; }
.about-biography p { margin-bottom: 0; }
.about-section-ending { max-width: 780px; margin: 28px auto 0; text-align: center; }
.about-section-ending strong { display: block; margin-top: 8px; color: var(--about-accent); }
.about-lead { font-size: 1.125rem; font-weight: 500; }
.about-emphasis { color: var(--about-accent); font-size: 1.25rem; font-weight: 600; }
.about-privacy { background: var(--about-surface); border: 1px solid var(--about-divider); border-radius: 12px; padding: 48px; }
.about-privacy .about-reading { margin: 0 auto; }
.about-privacy-statement { color: var(--about-accent); border-left: 3px solid var(--about-accent); padding: 4px 0 4px 24px; font-size: 1.35rem; font-weight: 600; line-height: 1.5; margin-top: 32px; }
.about-privacy .about-privacy-statement { margin-bottom: 0; }
.about-reflection { border-left: 2px solid var(--about-divider); padding-left: 24px; font-style: italic; }
.about-closing { padding: 48px; background: var(--about-tint); border-radius: 12px; text-align: center; }
.about-closing h2, .about-closing p { max-width: 760px; margin-left: auto; margin-right: auto; }
.about-closing-message { font-weight: 600; color: var(--about-accent); }
.about-closing .v-btn { margin-top: 12px; }

@media (max-width: 767px) {
  .about-self-biography { margin-top: 8px; margin-bottom: 32px; }
  .about-desktop-break { display: none; }
  .about-hero { padding: 32px 20px 12px; }
  .about-section { margin: 44px auto; }
  .about-section-heading { text-align: left; }
  .about-role-grid, .about-biographies, .about-principles { grid-template-columns: 1fr; }
  .about-role-grid { padding: 0; }
  .about-role { padding: 24px 0; }
  .about-role + .about-role { border-left: 0; border-top: 1px solid var(--about-divider); }
  .about-principles, .about-biography, .about-privacy, .about-closing { padding: 24px; }
  .about-ai-note, .about-section-ending { text-align: left; }
  .about-privacy-statement { padding-left: 16px; font-size: 1.2rem; }
}
</style>
